// CC-63 — Knowledge Base page: mirror of the Google Drive folder.
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, Search, FileSpreadsheet, Folder, ExternalLink, Download } from "lucide-react";
import PageHeader from "@/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import KnowledgeTree from "@/components/KnowledgeTree";
import {
  knowledgeApi, knowledgeKeys,
  formatBytes, exportFormatsFor,
} from "@/lib/knowledgeApi";

function formatRelative(iso) {
  if (!iso) return "never";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

function flattenTree(node, out = [], breadcrumb = []) {
  if (!node) return out;
  out.push({ ...node, _breadcrumb: breadcrumb });
  const nextCrumb = [...breadcrumb, node.name];
  for (const c of node.children || []) {
    flattenTree(c, out, nextCrumb);
  }
  return out;
}

export default function KnowledgeBase() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [nowTick, setNowTick] = useState(0); // force rerender for "X min ago"
  const syncStartedAtRef = useRef(null);

  // Live timer for the "last sync" indicator — tick every 60s.
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: knowledgeKeys.tree(),
    queryFn: knowledgeApi.getTree,
    refetchInterval: 60_000,
  });

  // Multi-root: prefer `roots` array when available, fall back to the
  // single `root` field for backward compatibility with older servers.
  const roots = useMemo(() => {
    if (Array.isArray(data?.roots)) return data.roots;
    return data?.root ? [data.root] : [];
  }, [data]);
  const hasRoots = roots.length > 0;
  const lastSync = data?.last_sync || null;
  const totalItems = data?.items_total ?? 0;

  // Search: flatten EVERY root + filter by name.
  const searchResults = useMemo(() => {
    if (!search.trim() || !hasRoots) return null;
    const q = search.trim().toLowerCase();
    const flat = roots.flatMap((r) => flattenTree(r));
    return flat
      .filter((n) => !n.is_folder && n.name.toLowerCase().includes(q));
  }, [search, roots, hasRoots]);

  async function handleSyncNow() {
    if (syncing) return;
    setSyncing(true);
    syncStartedAtRef.current = new Date();
    const toastId = toast.loading("Syncing with Google Drive…", {
      duration: Infinity,
    });
    try {
      await knowledgeApi.syncNow();
      // Poll sync-status until a run completed AFTER our start time.
      const started = syncStartedAtRef.current;
      const deadline = Date.now() + 30_000;
      let lastRun = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const s = await knowledgeApi.getSyncStatus();
          lastRun = s.last_run;
          if (lastRun
              && lastRun.completed_at
              && new Date(lastRun.completed_at) > started
              && lastRun.triggered_by === "manual") {
            break;
          }
        } catch (_e) { /* keep polling */ }
      }
      // Refetch the tree.
      await qc.invalidateQueries({ queryKey: knowledgeKeys.tree() });
      if (lastRun?.status === "success") {
        const parts = [];
        if (lastRun.items_added)   parts.push(`${lastRun.items_added} added`);
        if (lastRun.items_updated) parts.push(`${lastRun.items_updated} updated`);
        if (lastRun.items_removed) parts.push(`${lastRun.items_removed} removed`);
        toast.success(
          parts.length ? `Synced · ${parts.join(" · ")}` : "Synced · no changes",
          { id: toastId, duration: 4000 },
        );
      } else if (lastRun?.status === "error") {
        toast.error(`Sync failed: ${lastRun.error_message || "unknown"}`,
          { id: toastId, duration: 6000 });
      } else {
        toast.message("Sync still running in the background", { id: toastId, duration: 4000 });
      }
    } catch (e) {
      toast.error(`Sync failed: ${e.message || e}`, { id: toastId, duration: 6000 });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div data-testid="knowledge-base-page" className="max-w-5xl mx-auto">
      <PageHeader
        title="Knowledge Base"
        subtitle={
          <>
            Read-only mirror of the Google Drive folder <code className="mono text-[11px] text-hm-fg-muted">01 Knowledge Base</code>.
            Edits made in Drive appear here within 15 minutes (or hit Sync now).
          </>
        }
        badge="MIRROR"
        actions={
          <div className="flex items-center gap-3">
            <span
              key={nowTick /* refresh label */}
              data-testid="knowledge-last-sync"
              className="hidden sm:inline mono text-[11px] text-hm-fg-muted"
            >
              {lastSync?.completed_at
                ? `Last sync ${formatRelative(lastSync.completed_at)}`
                : "Not synced yet"}
            </span>
            <Button
              data-testid="knowledge-sync-now"
              size="sm"
              variant="outline"
              disabled={syncing}
              onClick={handleSyncNow}
              className="border-hm-border text-hm-fg hover:text-hm-fg hover:border-hm-border-strong"
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync now"}
            </Button>
          </div>
        }
      />

      {/* Search */}
      <div className="mb-4 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-hm-fg-subtle" />
        <Input
          data-testid="knowledge-search-input"
          type="search"
          placeholder="Search files…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-hm-bg-1 border-hm-border text-hm-fg placeholder:text-hm-fg-subtle focus-visible:ring-hm-accent/40"
        />
      </div>

      {/* States */}
      {isLoading && <TreeSkeleton />}

      {isError && (
        <div data-testid="knowledge-error" className="rounded-md border border-hm-danger/30 bg-hm-danger/5 p-4 text-sm text-hm-fg">
          Failed to load knowledge base. {String(error?.message || "")}
        </div>
      )}

      {!isLoading && !isError && !hasRoots && (
        <div data-testid="knowledge-empty" className="rounded-md border border-hm-border bg-hm-bg-1 p-6 text-center">
          <Folder className="h-6 w-6 text-hm-fg-subtle mx-auto mb-2" />
          <div className="text-sm text-hm-fg">No items yet.</div>
          <div className="text-xs text-hm-fg-muted mt-1">
            The initial sync hasn't finished. Try{" "}
            <button className="underline hover:text-hm-fg" onClick={handleSyncNow}>Sync now</button>.
          </div>
        </div>
      )}

      {!isLoading && !isError && hasRoots && (
        <>
          {search.trim() ? (
            <div className="rounded-md border border-hm-border bg-hm-bg overflow-hidden">
              <SearchResults results={searchResults} query={search} />
            </div>
          ) : (
            <div className="space-y-4" data-testid="knowledge-roots">
              {roots.map((r) => (
                <div
                  key={r.drive_id}
                  data-testid={`knowledge-root-${r.drive_id}`}
                  className="rounded-md border border-hm-border bg-hm-bg overflow-hidden"
                >
                  <KnowledgeTree root={r} />
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Footer stats */}
      {!isLoading && !isError && hasRoots && (
        <div className="mt-4 flex items-center justify-between text-xs text-hm-fg-subtle">
          <span>
            {totalItems} item{totalItems === 1 ? "" : "s"} total
            {roots.length > 1 ? ` · ${roots.length} roots` : ""}
          </span>
          {lastSync && (
            <span className="mono">
              last run · {lastSync.status} · trigger={lastSync.triggered_by}
              {lastSync.items_added || lastSync.items_updated || lastSync.items_removed
                ? ` · +${lastSync.items_added} ~${lastSync.items_updated} -${lastSync.items_removed}`
                : ""}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function SearchResults({ results, query }) {
  if (!results || results.length === 0) {
    return (
      <div data-testid="knowledge-search-empty" className="p-6 text-center text-sm text-hm-fg-muted">
        No files match <span className="text-hm-fg">&ldquo;{query}&rdquo;</span>.
      </div>
    );
  }
  return (
    <ul data-testid="knowledge-search-results" className="divide-y divide-hm-border/50">
      {results.map((item) => {
        const formats = exportFormatsFor(item.mime_type);
        const crumbs = (item._breadcrumb || []).slice(1).join(" › ");
        return (
          <li key={item.drive_id} className="flex items-center gap-3 px-3 py-2 hover:bg-hm-bg-1">
            <FileSpreadsheet className="h-4 w-4 text-emerald-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-hm-fg">{item.name}</div>
              {crumbs && (
                <div className="truncate text-[11px] text-hm-fg-subtle mono mt-0.5">
                  {crumbs}
                </div>
              )}
            </div>
            <span className="hidden sm:inline mono text-[11px] text-hm-fg-subtle">
              {formatBytes(item.size_bytes)}
            </span>
            {formats ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 text-xs border-hm-border">
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    Download
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px]">
                  {formats.map(({ format, label }) => (
                    <DropdownMenuItem key={format} asChild>
                      <a href={knowledgeApi.downloadUrl(item.drive_id, format)}>
                        {label}
                      </a>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <a
                href={knowledgeApi.downloadUrl(item.drive_id)}
                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-hm-border text-hm-fg-muted hover:text-hm-fg"
                title="Download"
              >
                <Download className="h-3.5 w-3.5" />
              </a>
            )}
            {item.drive_url && (
              <a
                href={item.drive_url} target="_blank" rel="noreferrer noopener"
                className="inline-flex items-center justify-center h-8 w-8 rounded-md border border-hm-border text-hm-fg-muted hover:text-hm-fg"
                title="Open in Drive"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function TreeSkeleton() {
  return (
    <div data-testid="knowledge-skeleton" className="rounded-md border border-hm-border bg-hm-bg">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="flex items-center gap-3 px-3 py-2 border-b border-hm-border/50 last:border-b-0"
          style={{ paddingLeft: `${12 + (i === 0 ? 0 : 20)}px` }}
        >
          <div className="h-4 w-4 rounded bg-hm-bg-2 animate-pulse" />
          <div className="h-4 w-48 rounded bg-hm-bg-2 animate-pulse" />
          <div className="h-3 w-20 rounded bg-hm-bg-2/60 animate-pulse ml-auto" />
        </div>
      ))}
    </div>
  );
}
